import type { LocaleCode, OrderingBlock } from '@fixit/bozp-content';
import { useRef, useState } from 'react';
import { resolveLocalized } from '../locale';

/**
 * Ungraded engagement widget — renders the prompt + items as a reorderable list
 * (move-up/move-down, keyboard-accessible, no drag library). Reports the FIRST
 * engagement via onInteract(block.id) — a reorder OR the explicit "the order is
 * correct" confirm, because an authored list may already be in the right order
 * (chapter `ord-prvni-pomoc` is) and agreeing with it must satisfy the chapter
 * gate without first breaking the list. The chosen order is NOT graded or
 * reported.
 */
export function OrderingBlockView({
  block,
  locale,
  onInteract,
}: {
  block: OrderingBlock;
  locale: LocaleCode;
  onInteract?: (widgetId: string) => void;
}) {
  const [order, setOrder] = useState<string[]>(() => block.items.map((i) => i.id));
  /** Confirmation is about the order currently on screen — a later move clears it. */
  const [confirmed, setConfirmed] = useState(false);
  const fired = useRef(false);
  const en = locale === 'en-US';
  const labelOf = (id: string) =>
    resolveLocalized(block.items.find((i) => i.id === id)?.label ?? { 'cs-CZ': id }, locale);

  /** One-shot across BOTH paths: a move and a confirm together still report once. */
  const reportEngagement = () => {
    if (fired.current) return;
    fired.current = true;
    onInteract?.(block.id);
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= order.length) return;
    const next = [...order];
    [next[index], next[target]] = [next[target], next[index]];
    setOrder(next);
    setConfirmed(false);
    reportEngagement();
  };

  const confirmOrder = () => {
    setConfirmed(true);
    reportEngagement();
  };

  return (
    <div data-block-type="ordering">
      <p>{resolveLocalized(block.prompt, locale)}</p>
      <ol>
        {order.map((id, index) => {
          const label = labelOf(id);
          return (
            <li key={id} data-item-id={id}>
              <span>{label}</span>
              <button
                type="button"
                aria-label={`Posunout nahoru: ${label}`}
                onClick={() => move(index, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                aria-label={`Posunout dolů: ${label}`}
                onClick={() => move(index, 1)}
              >
                ↓
              </button>
            </li>
          );
        })}
      </ol>
      <button
        type="button"
        data-ordering-confirm
        data-confirmed={confirmed ? 'true' : undefined}
        data-testid="ordering-confirm"
        disabled={confirmed}
        onClick={confirmOrder}
      >
        {confirmed
          ? en
            ? '✓ Order confirmed'
            : '✓ Pořadí potvrzeno'
          : en
            ? 'The order is correct'
            : 'Pořadí je správné'}
      </button>
    </div>
  );
}
